import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api, trackEvent } from './shared/utils/api';
import { Close, ArrowRight, Check } from './Icons';
import { InfoTooltip } from './InfoTooltip';

const RATINGS = [
  { value: 4, label: 'Full recall',   desc: 'Recalled everything with accuracy',   color: '#4caf7d', encouragement: 'This topic is holding. Keep it in your review rotation.' },
  { value: 3, label: 'Mostly right', desc: 'Minor gaps, core understanding solid', color: '#7cb8a0', encouragement: 'Some gaps in the detail. Review and try again next week.' },
  { value: 2, label: 'Some gaps',    desc: 'Recalled parts but missed key points', color: '#d4903a', encouragement: 'Partial recall. Add this to your planner for another pass.' },
  { value: 1, label: 'Blank',        desc: 'Could not recall the content',         color: '#e05555', encouragement: 'Add this to your planner and try again in a few days.' },
];

const TIMER_SECONDS = 180; // 3 minutes

function Timer({ running, elapsed, onToggle }) {
  const remaining = TIMER_SECONDS - elapsed;
  const mins = Math.floor(Math.abs(remaining) / 60);
  const secs = Math.abs(remaining) % 60;
  const over = remaining < 0;
  const pct = Math.min(100, (elapsed / TIMER_SECONDS) * 100);
  const color = over ? 'var(--amber)' : elapsed > TIMER_SECONDS * 0.75 ? 'var(--amber)' : 'var(--accent)';

  return (
    <div className="recall-timer">
      <div className="recall-timer-bar-wrap">
        <div className="recall-timer-bar" style={{ width: pct + '%', background: color }} />
      </div>
      <div className="recall-timer-row">
        <span className="recall-timer-display" style={{ color }}>
          {over ? '+' : ''}{mins}:{secs.toString().padStart(2, '0')}
        </span>
        <button className="recall-timer-btn" onClick={onToggle}>
          {running ? 'Pause' : 'Start'}
        </button>
      </div>
    </div>
  );
}

function BulletEditor({ value, onChange }) {
  const textareaRef = useRef(null);
  const pendingCursor = useRef(null);

  // Place cursor at end on mount so it's after "• ", ready to type
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      const end = el.value.length;
      el.selectionStart = end;
      el.selectionEnd = end;
    }
  }, []); // eslint-disable-line

  // Apply pending cursor position after render (for Enter key)
  useEffect(() => {
    if (pendingCursor.current !== null && textareaRef.current) {
      textareaRef.current.selectionStart = textareaRef.current.selectionEnd = pendingCursor.current;
      pendingCursor.current = null;
    }
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const textarea = textareaRef.current;
      const pos = textarea.selectionStart;
      const before = value.slice(0, pos);
      const after = value.slice(pos);
      const inserted = '\n• ';
      onChange(before + inserted + after);
      pendingCursor.current = pos + inserted.length;
    }
  };

  return (
    <textarea
      ref={textareaRef}
      className="recall-textarea"
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder="• Start typing what you remember&#10;• Press Enter to add a new bullet"
      autoFocus
    />
  );
}

export default function Recall() {
  const [topics, setTopics] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [phase, setPhase] = useState('select'); // select | recall | reveal | done
  const [notes, setNotes] = useState('• ');
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [rating, setRating] = useState(null);
  const [actualConf, setActualConf] = useState(null);
  const [sessions, setSessions] = useState([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    Promise.all([api('/api/topics/FLK1'), api('/api/topics/FLK2')]).then(([f1, f2]) => {
      setTopics([...f1, ...f2]);
    });
  }, []);

  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [timerRunning]);

  const startRecall = (topic) => {
    setSelectedTopic(topic);
    setNotes('• ');
    setElapsed(0);
    setTimerRunning(true);
    setPhase('recall');
    setRating(null);
  };

  const handleReveal = () => {
    setTimerRunning(false);
    setPhase('reveal');
  };

  const handleRate = async (r) => {
    setRating(r);

    // Backend computes the blend and returns the updated confidence
    const recallRes = await api('/api/recall', {
      method: 'POST',
      body: JSON.stringify({
        topic_id: selectedTopic.id,
        duration_seconds: elapsed,
        self_rating: r.value,
        notes_written: notes,
      }),
    });

    const newConf = recallRes?.updated_confidence ?? selectedTopic.confidence;
    setActualConf(newConf);

    trackEvent('first_recall_session', { rating: r.value });

    setSessions(prev => [{
      topic: selectedTopic.subtopic,
      rating: r.label,
      color: r.color,
      conf: newConf !== null ? Math.round(newConf * 100) : null,
      encouragement: r.encouragement,
      time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    }, ...prev.slice(0, 9)]);

    setPhase('done');
  };

  const filteredTopics = topics.filter(t => {
    const q = search.toLowerCase();
    return !q || (t.subtopic||'').toLowerCase().includes(q) || (t.topic||'').toLowerCase().includes(q) || (t.module||'').toLowerCase().includes(q);
  });

  if (phase === 'recall' && selectedTopic) {
    return (
      <div className="recall-page">
        <div className="recall-header">
          <div>
            <div className="recall-phase-label">Active recall</div>
            <div className="recall-topic-name">{selectedTopic.subtopic}</div>
            <div className="recall-topic-meta">{selectedTopic.module} · {selectedTopic.topic}</div>
          </div>
          <button className="recall-exit-btn" onClick={() => { setTimerRunning(false); setPhase('select'); }}><Close size={12} /> Exit</button>
        </div>

        <Timer running={timerRunning} elapsed={elapsed} onToggle={() => setTimerRunning(r => !r)} />

        <div className="recall-instruction">Write down everything you remember about this topic. No notes, no peeking.</div>

        <BulletEditor value={notes} onChange={setNotes} />

        <button className="recall-reveal-btn" onClick={handleReveal}>Show topic detail <ArrowRight size={14} /></button>
      </div>
    );
  }

  if (phase === 'reveal' && selectedTopic) {
    return (
      <div className="recall-page">
        <div className="recall-header">
          <div>
            <div className="recall-phase-label">Compare and rate</div>
            <div className="recall-topic-name">{selectedTopic.subtopic}</div>
          </div>
        </div>

        <div className="recall-compare">
          <div className="recall-compare-col">
            <div className="recall-compare-label">What you wrote</div>
            <div className="recall-compare-content">
              {notes.trim()
                ? notes.split('\n').map((line, i) => (
                    <div key={i} className="recall-bullet-line">{line || '\u00a0'}</div>
                  ))
                : <em style={{ color: 'var(--hint)' }}>Nothing written</em>
              }
            </div>
          </div>
          <div className="recall-compare-col">
            <div className="recall-compare-label">Topic detail</div>
            <div className="recall-compare-content">
              {(selectedTopic.detail || '').split('\n').map((line, i) => (
                <div key={i} className="recall-bullet-line">{line || '\u00a0'}</div>
              ))}
            </div>
            {selectedTopic.notes && (
              <div className="recall-compare-notes">Your notes: {selectedTopic.notes}</div>
            )}
          </div>
        </div>

        <div className="recall-rate-section">
          <div className="recall-rate-label">How did you do?</div>
          <div className="recall-ratings">
            {RATINGS.map(r => (
              <button key={r.value} className="recall-rating-btn"
                style={{ borderColor: r.color + '55', background: r.color + '11' }}
                onClick={() => handleRate(r)}>
                <span className="recall-rating-label" style={{ color: r.color }}>{r.label}</span>
                <span className="recall-rating-desc">{r.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'done') {
    const encouragement = rating?.encouragement || (rating?.value >= 3
      ? 'This topic is holding. Keep it in your review rotation.'
      : `Go back to your notes on ${selectedTopic?.subtopic} and fill in what you missed. Try again in a few days.`);
    return (
      <div className="recall-page">
        <div className="recall-done">
          <div className="recall-done-icon" style={{ color: rating?.color }}><Check size={40} /></div>
          <div className="recall-done-title">Session saved</div>
          <div className="recall-done-sub">
            Rated <strong style={{ color: rating?.color }}>{rating?.label}</strong> · confidence updated to {actualConf !== null ? Math.round(actualConf * 100) : '—'}%
          </div>
          {rating && (
            <div className="recall-encouragement" style={{ borderLeftColor: rating.color }}>
              {encouragement}
            </div>
          )}
          <div className="recall-done-actions">
            <button className="recall-reveal-btn" onClick={() => setPhase('select')}>Practice another topic</button>
          </div>
          {sessions.length > 0 && (
            <div className="recall-session-log">
              <div className="recall-session-log-title">Today's sessions</div>
              {sessions.map((s, i) => (
                <div key={i} className="recall-session-row">
                  <span style={{ color: s.color, fontWeight: 500, fontSize: 12 }}>{s.rating}</span>
                  <span style={{ fontSize: 13 }}>{s.topic}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: s.color }}>{s.conf}%</span>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{s.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Select phase
  return (
    <div className="recall-page">
      <div className="recall-intro">
        <div className="recall-intro-title">Active recall <InfoTooltip text="Active recall practice. You pick a topic, write what you remember in 3 minutes without notes, then compare against the reference material. Your self-rating updates your confidence score for that topic." /></div>
        <p className="recall-intro-text">
          Select a topic and write down everything you remember. Three minutes, no notes. Then compare against
          the topic detail and rate yourself. Your confidence updates using a blended calculation so one session
          will not override your history.
        </p>
      </div>

      <input className="search-input" placeholder="Search topics"
        value={search} onChange={e => setSearch(e.target.value)} />

      <div className="recall-topic-list">
        {filteredTopics.slice(0, 60).map(t => (
          <div key={t.id} className="recall-topic-row" onClick={() => startRecall(t)}>
            <div className="recall-topic-info">
              <span className="recall-topic-sub">{t.subtopic}</span>
              <span className="recall-topic-meta">
                <span style={{ color: t.module === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)', fontSize: 10, fontWeight: 600 }}>{t.module}</span>
                {' · '}{t.topic}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {t.confidence !== null && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: t.confidence < 0.5 ? 'var(--red)' : t.confidence < 0.7 ? 'var(--amber)' : 'var(--green)' }}>
                  {Math.round(t.confidence * 100)}%
                </span>
              )}
              <span className="recall-start-btn">Practice <ArrowRight size={12} /></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
